import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Assistant, CompletionMessage } from '@app-libs/model';
import { IAssistantInstructions } from './assistant.interfaces';
import { ChatStateService } from '../chat-state.service';
import { AuthService } from '../../auth/auth.service';
import { InstructionsApiService } from '../../api/instructions-api.service';

/**
 * AssistantInstructions - Handles assistant instructions and welcome messages
 *
 * This service will handle:
 * - Welcome message generation
 * - Instruction creation from system messages
 * - System instruction retrieval
 * - Instruction set building
 */
@Injectable({
  providedIn: 'root',
})
export class AssistantInstructions implements IAssistantInstructions {
  constructor(
    private stateService: ChatStateService,
    private authService: AuthService,
    private instructionsApiService: InstructionsApiService,
  ) {}

  /**
   * Build welcome message for assistant
   * (Migrated from AssistantContext - now uses backend API service)
   */
  buildWelcomeMessage(assistant: Assistant): Observable<string> {
    // Get user profile and call backend service to build welcome message
    return from(this.authService.getProfile()).pipe(
      switchMap((profile) => 
        this.instructionsApiService.buildWelcomeMessage(assistant, profile)
      )
    );
  }

  /**
   * Create instruction from system messages
   * (Migrated from ChatApiService.createInstructionFromSystemMessages - exact same logic)
   */
  createInstructionFromSystemMessages(messages: Partial<CompletionMessage>[]): Observable<string | undefined> {
    // Find system messages
    const systemMessages = messages
      .filter((message) => message.role === 'system')
      .map((message) => message);

    let instruction = this.stateService.assistant.instructions || undefined;

    // If we have system messages, use them as the instruction
    if (systemMessages.length > 0) {
      instruction = '';
      systemMessages.forEach((message) => {
        instruction = instruction + '\n' + message.content;
      });
    }
    
    // Return exactly as original: instruction could be undefined
    return of(instruction);
  }

  /**
   * Get system instructions for assistant
   * Uses backend assembleInstructions API if assistant has a prompt_id
   */
  getSystemInstructions(assistantId: string): Observable<string> {
    // Get the current assistant from state
    const assistant = this.stateService.assistant;
    
    if (!assistant || assistant.assistant_id !== assistantId) {
      return of(''); // No matching assistant found
    }
    
    // If assistant has a prompt_id, use backend to assemble instructions
    if (assistant.prompt_id) {
      return this.instructionsApiService.assembleInstructions(assistant.prompt_id);
    }
    
    // Otherwise return the assistant's instructions or empty string
    return of(assistant.instructions || '');
  }

  /**
   * Build complete instruction set
   * Combines assistant instructions with system messages to create a complete instruction set
   */
  buildInstructionSet(assistant: Assistant, systemMessages: CompletionMessage[]): Observable<string> {
    // Start with assistant's base instructions
    let instructionSet = assistant.instructions || '';
    
    // Add system messages if any
    if (systemMessages && systemMessages.length > 0) {
      const systemInstructions = systemMessages
        .filter(msg => msg.role === 'system' && msg.content)
        .map(msg => msg.content)
        .join('\n');
      
      if (systemInstructions) {
        // Combine base instructions with system messages
        if (instructionSet) {
          instructionSet += '\n\n' + systemInstructions;
        } else {
          instructionSet = systemInstructions;
        }
      }
    }
    
    return of(instructionSet);
  }
}
